import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronDown, Copy, Check, Info } from 'lucide-react';
import { NODE_TYPES } from './nodeTypes';

const TYPE_BADGE_COLORS = {
  string: 'bg-gray-100 text-gray-600',
  number: 'bg-blue-100 text-blue-600',
  array: 'bg-amber-100 text-amber-600',
  object: 'bg-purple-100 text-purple-600',
  boolean: 'bg-green-100 text-green-600',
  null: 'bg-gray-100 text-gray-400',
};

function getType(value) {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function TypeBadge({ type }) {
  const colors = TYPE_BADGE_COLORS[type] || TYPE_BADGE_COLORS.null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0 text-[10px] font-medium rounded ${colors}`}>
      {type}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-200"
      title={`Copy ${text}`}
    >
      {copied ? (
        <Check className="w-3 h-3 text-green-500" />
      ) : (
        <Copy className="w-3 h-3 text-gray-400" />
      )}
    </button>
  );
}

function formatValue(value, type) {
  switch (type) {
    case 'string': {
      const display = value.length > 120 ? value.slice(0, 120) + '...' : value;
      return <span className="text-green-700">"{display}"</span>;
    }
    case 'number':
      return <span className="text-blue-600">{value}</span>;
    case 'boolean':
      return (
        <span className={`inline-flex items-center px-1.5 py-0 text-[10px] font-medium rounded ${value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {String(value)}
        </span>
      );
    case 'null':
      return <span className="text-gray-400 italic">null</span>;
    default:
      return <span className="text-gray-500">{String(value)}</span>;
  }
}

const MAX_DEPTH = 6;
const MAX_ARRAY_ITEMS = 20;

function JsonTreeNode({ keyName, value, path, nodeId, depth, defaultOpen }) {
  const type = getType(value);
  const isExpandable = type === 'object' || type === 'array';
  const [expanded, setExpanded] = useState(defaultOpen ?? depth === 0);

  if (depth > MAX_DEPTH) {
    return (
      <div className="py-0.5 group flex items-center gap-1.5 font-mono text-[11px]" style={{ paddingLeft: `${depth * 16}px` }}>
        {keyName !== null && <span className="text-gray-500 font-medium">{keyName}:</span>}
        <pre className="text-gray-400 text-[10px] truncate max-w-xs">{JSON.stringify(value)}</pre>
      </div>
    );
  }

  if (!isExpandable) {
    const refPath = path ? `{{${nodeId}.${path}}}` : null;
    return (
      <div className="py-0.5 group flex items-center gap-1.5 font-mono text-[11px]" style={{ paddingLeft: `${depth * 16}px` }}>
        {refPath && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <CopyButton text={refPath} />
          </span>
        )}
        {keyName !== null && <span className="text-gray-500 font-medium">{keyName}:</span>}
        <span className="truncate">{formatValue(value, type)}</span>
        <TypeBadge type={type} />
      </div>
    );
  }

  // Expandable: object or array
  const entries = type === 'array' ? value.map((v, i) => [i, v]) : Object.entries(value || {});
  const totalCount = entries.length;
  const displayEntries = type === 'array' && totalCount > MAX_ARRAY_ITEMS
    ? entries.slice(0, MAX_ARRAY_ITEMS)
    : entries;
  const hiddenCount = totalCount - displayEntries.length;
  const summary = type === 'array' ? `[${totalCount} item${totalCount !== 1 ? 's' : ''}]` : `{${totalCount} key${totalCount !== 1 ? 's' : ''}}`;

  return (
    <div>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full py-0.5 flex items-center gap-1.5 font-mono text-[11px] hover:bg-gray-50 rounded group"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        {expanded
          ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
          : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
        }
        {keyName !== null && <span className="text-gray-500 font-medium">{keyName}:</span>}
        <TypeBadge type={type} />
        <span className="text-[10px] text-gray-400">{summary}</span>
      </button>
      {expanded && (
        <div className={depth > 0 ? 'border-l border-gray-150 ml-1.5' : ''} style={depth > 0 ? { marginLeft: `${depth * 16 + 6}px` } : undefined}>
          {displayEntries.map(([key, val]) => {
            const childPath = type === 'array'
              ? (path ? `${path}[${key}]` : `[${key}]`)
              : (path ? `${path}.${key}` : `${key}`);
            return (
              <JsonTreeNode
                key={key}
                keyName={type === 'array' ? key : key}
                value={val}
                path={childPath}
                nodeId={nodeId}
                depth={depth + 1}
                defaultOpen={false}
              />
            );
          })}
          {hiddenCount > 0 && (
            <div
              className="py-0.5 text-[10px] text-gray-400 italic font-mono"
              style={{ paddingLeft: `${(depth + 1) * 16}px` }}
            >
              ...and {hiddenCount} more item{hiddenCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function NodeOutputInspector({ nodeId, nodeType, outputData, onClose }) {
  const typeDef = NODE_TYPES[nodeType];
  const Icon = typeDef?.icon;

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isEmpty = !outputData || (typeof outputData === 'object' && Object.keys(outputData).length === 0);

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden" style={{ maxHeight: '80vh' }}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
          {Icon && (
            <div
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: typeDef?.color || '#94a3b8' }}
            />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">
              {typeDef?.label || nodeType} Output
            </h2>
            <span className="text-[10px] text-gray-400 font-mono">{nodeId}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner */}
        <div className="mx-5 mt-3 flex items-start gap-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-lg">
          <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-gray-600">
            Click the copy icon next to any value to copy its reference path for use in other nodes.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <p className="text-sm font-medium">No output data</p>
              <p className="text-xs mt-1">This node did not produce any output.</p>
            </div>
          ) : (
            <div>
              {Object.entries(outputData).map(([key, val]) => (
                <JsonTreeNode
                  key={key}
                  keyName={key}
                  value={val}
                  path={key}
                  nodeId={nodeId}
                  depth={0}
                  defaultOpen={true}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
